'use client';

import { asText } from '@prismicio/client';
import { useCookies } from 'react-cookie';
import { useParams, usePathname } from 'next/navigation';
import { createContext, useContext, useEffect, useState } from 'react';

import {
  pathWithoutLocale,
  MappedLocaleOption,
  ThemeToggle_ColourScheme_Key,
} from '@preventx/prismic-services';

// Types
import {
  Context,
  ProviderProps,
} from '@/types/navigation/siteNavigationContext';
import { PageParams } from '@/types/routing';

// Constants
import { HomeClassName } from '@/constants/configuration';

export const SiteNavigationContext = createContext<Context>({
  locales: [],
  navigation: [],
});

export const SiteNavigationProvider = ({
  children,
  navigation,
  globalLanguages = [],
  repositoryLanguages = [],
}: ProviderProps) => {
  const pathname = usePathname();
  const { lang } = useParams<PageParams>();
  const [cookies] = useCookies([ThemeToggle_ColourScheme_Key]);

  const [locales, setLocales] = useState<MappedLocaleOption[]>([]);

  const retrieveLocales = async () => {
    // Note : Selector is redundant when only one language to pick from so in this scenario just clear it out.
    if (repositoryLanguages.length === 0 || globalLanguages.length <= 1) {
      setLocales([]);

      return;
    }

    const mappedLocales = globalLanguages.map(
      ({ label, locale_iso_code }, index): MappedLocaleOption | null => {
        const hasMatchingLanguage = repositoryLanguages.some(
          (supportedLanguage) =>
            supportedLanguage.id.toLowerCase() ===
            locale_iso_code?.toLowerCase(),
        );

        if (!hasMatchingLanguage) {
          return null;
        }

        const path = pathWithoutLocale(pathname);

        return {
          label: asText(label),
          url: `/${locale_iso_code}/${path}`,
          id: `${locale_iso_code}-${index + 1}`,
        } satisfies MappedLocaleOption;
      },
    );

    const items = mappedLocales.filter((locale) => locale !== null);

    setLocales(items.length > 1 ? items : []);
  };

  const setClassList = () => {
    const classList = document.body.classList;

    // Purpose  :   All root pages require the home classname to be bound to the body
    if (pathname === '/' || pathname === `/${lang}`) {
      classList.add(HomeClassName);
    } else {
      classList.remove(HomeClassName);
    }
  };

  useEffect(() => {
    setClassList();
    retrieveLocales();
  }, [pathname]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    document.documentElement.setAttribute(
      'color-scheme',
      cookies['colour-scheme'] ?? 'auto',
    );
  }, []);

  return (
    <SiteNavigationContext.Provider
      value={{ locales: locales, navigation: navigation }}
    >
      {children}
    </SiteNavigationContext.Provider>
  );
};

export const useSiteNavigationContext = () =>
  useContext<Context>(SiteNavigationContext);
